import React, { Fragment, useState, useEffect } from 'react';
import { Form, Button } from 'react-bootstrap';

const Input = (props) => {
    let inputElement = null;
    const [alertVisible, setAlertVisible] = useState(false);

    useEffect(() => {
        if (props.label && props.elementType === 'label') {
            setAlertVisible(true);
            const timer = setTimeout(() => {
                setAlertVisible(false);
            }, props.duration || 4000);
            return () => clearTimeout(timer);
        } else {
            setAlertVisible(false);
        }
    }, [props.label, props.elementType, props.duration]);

    const minLength = props.minLength ? props.minLength : 0;
    const maxLength = props.maxLength ? props.maxLength : 15;
    const name = props.name;
    const required = props.required;
    const disabled = props.disabled ? props.disabled : false;
    const options = props.options ? props.options : [];
    const buttonType = props.type ? props.type : 'submit';
    const buttonValue = props.value ? props.value : 'Submit';
    const fieldValue = props.value ? props.value : '';
    const readOnly = props.readOnly ? props.readOnly : false;
    const errors = props.errors;
    const pattern = props.pattern ? props.pattern : '';
    let errorMessage = '';
    const style = props.style ? props.style : {};

    const onChange = (e) => {
        if (props.onChange) {
            props.onChange(e);
        }
    };

    const onClick = () => {
        if (props.onClick) {
            props.onClick();
        }
    };

    const selectOptions = () => {
        return options.map(option => <option key={option.value} value={option.value}>{option.name}</option>);
    };

    const renderComponent = () => {
        if (props.label && props.elementType !== 'label') {
            return (
                <Fragment>
                    <Form.Label className="custom-form-label">
                        {props.label}
                        {required && <span className="text-danger ms-1">*</span>}
                    </Form.Label>
                    {inputElement}
                </Fragment>
            );
        }

        return inputElement;
    };

    if (errors && errors[name]) {
        if (errors[name].type === 'required')
            errorMessage = 'This field cannot be empty!';
        else if (errors[name].type === 'minLength')
            errorMessage = 'Minimum field length should be ' + minLength + ' characters';
        else if (errors[name].type === 'maxLength')
            errorMessage = 'Field length cannot exceed ' + maxLength + ' characters';
        else if (errors[name].type === 'pattern')
            errorMessage = 'Email should be in the format abc@pqr.com';
    }

    const registerProps = props.register && name
        ? props.register(name, { required: required, minLength: minLength, maxLength: maxLength, pattern: pattern })
        : {};

    switch (props.elementType) {
        case 'text':
            inputElement = (
                <Form.Group className="mb-3">
                    <Form.Control
                        type="text"
                        autoComplete="off"
                        minLength={minLength}
                        readOnly={readOnly}
                        maxLength={maxLength}
                        placeholder={props.placeholder}
                        onChange={props.onChange}
                        defaultValue={props.value}
                        {...registerProps}
                    />
                    {errorMessage && <Form.Text className="text-danger d-block mt-1">{errorMessage}</Form.Text>}
                </Form.Group>
            );
            break;

        case 'number':
            inputElement = (
                <Form.Group className="mb-3">
                    <Form.Control
                        type="number"
                        autoComplete="off"
                        min={minLength}
                        readOnly={readOnly}
                        max={maxLength}
                        placeholder={props.placeholder}
                        onChange={props.onChange}
                        defaultValue={props.value}
                        {...registerProps}
                    />
                    {errorMessage && <Form.Text className="text-danger d-block mt-1">{errorMessage}</Form.Text>}
                </Form.Group>
            );
            break;

        case 'color':
            inputElement = (
                <Form.Group className="mb-3">
                    <Form.Control
                        type="color"
                        autoComplete="off"
                        minLength={minLength}
                        readOnly={readOnly}
                        maxLength={maxLength}
                        placeholder={props.placeholder}
                        onChange={props.onChange}
                        value={props.value}
                        {...registerProps}
                    />
                    {errorMessage && <Form.Text className="text-danger d-block mt-1">{errorMessage}</Form.Text>}
                </Form.Group>
            );
            break;

        case 'date':
            inputElement = (
                <Form.Group className="mb-3">
                    <Form.Control
                        type="date"
                        autoComplete="off"
                        minLength={minLength}
                        readOnly={readOnly}
                        maxLength={maxLength}
                        placeholder={props.placeholder}
                        onChange={props.onChange}
                        defaultValue={props.value}
                        {...registerProps}
                    />
                    {errorMessage && <Form.Text className="text-danger d-block mt-1">{errorMessage}</Form.Text>}
                </Form.Group>
            );
            break;

        case 'password':
            inputElement = (
                <Form.Group className="mb-3">
                    <Form.Control
                        type="password"
                        minLength={minLength}
                        maxLength={maxLength}
                        readOnly={readOnly}
                        placeholder={props.placeholder}
                        onBlur={props.onBlur}
                        onChange={props.onChange}
                        {...registerProps}
                    />
                    {errorMessage && <Form.Text className="text-danger d-block mt-1">{errorMessage}</Form.Text>}
                </Form.Group>
            );
            break;

        case 'email':
            inputElement = (
                <Form.Group className="mb-3">
                    <Form.Control
                        type="email"
                        minLength={minLength}
                        maxLength={maxLength}
                        readOnly={readOnly}
                        placeholder={props.placeholder}
                        onChange={props.onChange}
                        {...registerProps}
                    />
                    {errorMessage && <Form.Text className="text-danger d-block mt-1">{errorMessage}</Form.Text>}
                </Form.Group>
            );
            break;

        case 'textarea':
            inputElement = (
                <Form.Group className="mb-3">
                    <Form.Control
                        as="textarea"
                        rows={3}
                        minLength={minLength}
                        maxLength={maxLength}
                        placeholder={props.placeholder}
                        onChange={props.onChange}
                        defaultValue={fieldValue}
                        {...registerProps}
                    />
                    {errorMessage && <Form.Text className="text-danger d-block mt-1">{errorMessage}</Form.Text>}
                </Form.Group>
            );
            break;

        case 'checkbox':
            inputElement = (
                <Form.Group className="mb-3">
                    <Form.Check
                        type="checkbox"
                        onChange={props.onChange}
                        label={props.value}
                        checked={props.checked}
                    />
                </Form.Group>
            );
            break;

        case 'button':
            inputElement = (
                <Button
                    style={style}
                    onClick={onClick}
                    variant={props.variant || 'primary'}
                    disabled={disabled}
                    type={buttonType}
                    className={`btn btn-${props.variant || 'primary'} ${props.className || ''}`}
                >
                    {buttonValue}
                </Button>
            );
            break;

        case 'select':
            inputElement = (
                <Form.Group className="mb-3">
                    <Form.Control
                        as="select"
                        {...(props.register && name ? props.register(name, { onChange }, { required: required }) : { onChange })}
                    >
                        {selectOptions()}
                    </Form.Control>
                    {errorMessage && <Form.Text className="text-danger d-block mt-1">{errorMessage}</Form.Text>}
                </Form.Group>
            );
            break;

        case 'label':
            inputElement = (props.label && alertVisible) ? (
                <div
                    className={`alert alert-${props.variant || 'info'} d-inline-flex align-items-center justify-content-between gap-2 my-2 py-2 px-3 shadow-sm`}
                    style={{
                        fontSize: '0.875rem',
                        borderRadius: '10px',
                        fontWeight: '500',
                        animation: 'fadeIn 0.25s ease'
                    }}
                >
                    <span>{props.label}</span>
                    <button
                        type="button"
                        onClick={() => setAlertVisible(false)}
                        style={{
                            background: 'transparent',
                            border: 'none',
                            color: 'inherit',
                            cursor: 'pointer',
                            padding: '0 0 0 8px',
                            fontSize: '18px',
                            lineHeight: 1,
                            opacity: 0.6
                        }}
                        title="Dismiss"
                    >
                        &times;
                    </button>
                </div>
            ) : null;
            break;

        default:
            inputElement = (
                <Form.Group className="mb-3">
                    <Form.Control
                        type="text"
                        placeholder={props.placeholder}
                        onChange={props.onChange}
                    />
                </Form.Group>
            );
    }

    return renderComponent();
};

export default Input;